/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { Helmet } from "react-helmet-async";
import React, { useEffect, useState } from "react";
import FileUploadSection from "@/src/components/Form/Common/FileUploadSection/FileUploadSection";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import _map from "lodash/map";
import _forEach from "lodash/forEach";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import requestDocumentsPageStyle from "./requestDocumentsPageStyle";
import Paper from "@mui/material/Paper";
import ApiClient from "@/src/services/apiClient";
import { useNavigate } from "react-router";
import { getCaseLink, getProductData } from "@/src/services/productService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "@/src/hooks/useProductParams";
import useMediaObjectCombinationRequest from "@/src/hooks/useMediaObjectCombinationRequest";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Child } from "@/src/types/Entities/Child";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";

const RequestDocumentsPage = () => {
  const { productId } = useProductParams();
  const productApiUri = getProductData("divorce", "apiUri");
  const { data: divorce, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });

  if (isLoading) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }
  return <RequestDocumentsPageInner divorce={divorce} />;
};

type RequestDocumentsPageInnerProps = {
  divorce: AbstractCase;
};

const RequestDocumentsPageInner = ({ divorce }: RequestDocumentsPageInnerProps) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoadingInternal, setIsLoadingInternalInternal] = useState(false);

  const { productId } = useProductParams();
  const productApiUri = getProductData("divorce", "apiUri");

  let mediaObjectCombinationRequestTypes = [];

  if (divorce.marriageCertificate === null) {
    mediaObjectCombinationRequestTypes.push("marriageCertificateFiles");
  }

  if (divorce.numberChildren > 0) {
    divorce.children.forEach((child: Child) => {
      if (child.birthCertificate === null) {
        mediaObjectCombinationRequestTypes.push("birthCertificateFiles" + child.id);
      }
    });
  }

  const { mediaObjectCombinationRequests, isLoadingMediaObjectCombinationRequest, updateAction } = useMediaObjectCombinationRequest(
    divorce,
    mediaObjectCombinationRequestTypes,
  );

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refreshDivorce = () => {
    queryClient.invalidateQueries({
      queryKey: [productApiUri, productId],
    });
  };

  const triggerUpdate = async () => {
    setIsLoadingInternalInternal(true);
    await ApiClient.post(`/generate_requested_mediaobjects/divorce/${divorce.id}`);
    navigate(getCaseLink(divorce));
  };

  useEffect(() => {
    if (divorce) {
      let hasRequestedDocument = divorce.marriageCertificate === null;
      _forEach(divorce.children, (child) => {
        hasRequestedDocument = hasRequestedDocument || child.birthCertificate === null;
      });
      if (!hasRequestedDocument) {
        navigate(getCaseLink(divorce));
      }
    }
  }, [divorce, navigate]);

  if (isLoadingMediaObjectCombinationRequest) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  return (
    <LayoutProtectedRoutes>
      <Helmet>
        <title>Fehlende Dokumente | Legalbird</title>
      </Helmet>
      <ContentBox headline={<strong>Fehlende Dokumente ergänzen</strong>} headlineVariant={"h5"} showDivider={true}>
        Um Ihren Fall weiter bearbeiten zu können, benötigen wir noch folgende Unterlagen von Ihnen.
      </ContentBox>
      <Paper elevation={4} sx={requestDocumentsPageStyle.box}>
        {divorce.marriageCertificate === null && (
          <FileUploadSection
            mediaObjectCombinationRequest={mediaObjectCombinationRequests.marriageCertificateFiles}
            temporaryMediaObjectsFieldname={"marriageCertificateFiles"}
            headline={"Heiratsurkunde"}
            helperText={"Bitte laden Sie hier Ihre Heiratsurkunde hoch. Sollte Ihnen keine deutsche Heiratsurkunde vorliegen, benötigen Sie eine Übersetzung."}
            productEntity={divorce}
            productEntityId={divorce.id}
            product={"divorce"}
            updateAction={updateAction}
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            fieldName={"marriageCertificate"}
          />
        )}
        {_map(divorce.children, (child) => {
          if (child.birthCertificate !== null) {
            return null;
          }
          return (
            <FileUploadSection
              mediaObjectCombinationRequest={mediaObjectCombinationRequests["birthCertificateFiles" + child.id]}
              temporaryMediaObjectsFieldname={"birthCertificateFiles"}
              key={child.id}
              headline={"Geburtsurkunde " + child.name}
              helperText={"Bitte laden Sie hier die Geburtsurkunde hoch."}
              productEntity={child}
              productEntityId={divorce.id}
              product={"divorce"}
              updateAction={updateAction}
              setIsBlocked={setIsBlocked}
              isBlocked={isBlocked}
              postSubmitCallback={refreshDivorce}
              fieldName={"birthCertificate"}
            />
          );
        })}
        <br />
        <br />
        <ButtonLoading
          onClick={triggerUpdate}
          color="primary"
          variant="contained"
          disabled={false}
          fullWidth={true}
          type={"submit"}
          isLoading={isLoadingInternal}
        >
          Dokumente hochladen
        </ButtonLoading>
      </Paper>
    </LayoutProtectedRoutes>
  );
};

export default RequestDocumentsPage;
